import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { ReleaseListRow } from 'fym-shared/src/music24/releases';
import { TypedMemo } from '../../../../common/types';
import { releaseListMessages } from './releaseListMessages';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './ReleasesList.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ReleaseRowCountsProps {
  row: ReleaseListRow;
  /** True while the release day is today or later: "No pre-save" shows only then. */
  isUpcoming: boolean;
}

/**
 * The small counts on the right of a release row (design a1): the assets with "N new", the posts
 * done · planned, the SmartLink clicks and the pre-saves. No "N of 15" anywhere (owner 2026-10-07).
 */
const ReleaseRowCountsComponent: React.FC<ReleaseRowCountsProps> = ({ row, isUpcoming }) => {
  const intl = useIntl();
  const { assets, posts, smartLink, presave } = row;
  const figure = (value: number | null): string => (value === null ? '–' : intl.formatNumber(value));

  return (
    <>
      <div className={styles.count} data-testid="release-assets">
        <b className={styles.countFig}>{intl.formatNumber(assets.count)}</b>
        {intl.formatMessage(releaseListMessages.assets)}
        {assets.newCount > 0 && (
          <span className={styles.newBadge}>
            {intl.formatMessage(releaseListMessages.newCount, { count: assets.newCount })}
          </span>
        )}
      </div>
      <div className={styles.count} data-testid="release-posts">
        <b className={styles.countFig}>{`${intl.formatNumber(posts.done)} · ${intl.formatNumber(posts.planned)}`}</b>
        {intl.formatMessage(releaseListMessages.donePlanned)}
      </div>
      <div className={styles.links}>
        <span className={styles.linkLine}>
          <span className={clsx(styles.dot, smartLink === null && styles.dotOff)} />
          {smartLink === null ? (
            intl.formatMessage(releaseListMessages.noSmartLink)
          ) : (
            <>
              {intl.formatMessage(releaseListMessages.smartLink)}
              <span className={styles.fig}>{figure(smartLink.clicks)}</span>
            </>
          )}
        </span>
        {presave !== null && (
          <span className={styles.linkLine}>
            <span className={clsx(styles.dot, !presave.live && styles.dotEnd)} />
            {intl.formatMessage(presave.live ? releaseListMessages.presave : releaseListMessages.presaveEnded)}
            <span className={styles.fig}>{intl.formatNumber(presave.saves)}</span>
          </span>
        )}
        {presave === null && isUpcoming && (
          <span className={styles.linkLine}>
            <span className={clsx(styles.dot, styles.dotOff)} />
            {intl.formatMessage(releaseListMessages.noPresave)}
          </span>
        )}
      </div>
    </>
  );
};

export const ReleaseRowCounts = TypedMemo(ReleaseRowCountsComponent);
